import { Subject } from 'rxjs';
import generateGUID from 'utils/guidGenerator';
import { formatNumericInput, formatWithCommas, hasMinMaxConflict, parseDigits } from 'utils/numericInput';

const FIELDS = ['min', 'max'];

function createFieldHtml({ field, label, placeholder }) {
  const inputId = `price-filter-${field}-${generateGUID()}`;

  return `
    <div class="nhs-c-input__wrapper nhs-c-input__wrapper--filter">
      <label class="nhs-c-input__label nhs-c-input__label--book nhs-u-color--black-900" for="${inputId}">${label}</label>
      <div class="nhs-c-input__field nhs-c-input__field--outlined nhs-c-input__field--prefixed"
           data-price-field="${field}">
        <span class="nhs-c-input__prefix" data-prefix hidden>$</span>
        <input type="text"
               id="${inputId}"
               class="nhs-c-input__field-input"
               placeholder="${placeholder}"
               inputmode="numeric"
               autocomplete="off"
               data-price-input="${field}"
               data-qa="price_filter_${field}" />
      </div>
    </div>
  `;
}

/**
 * Price filter — Min/Max text fields (Figma "Price" group). Only the two fields are built; the
 * price distribution graph and the dual-handle slider of the full "'Price' Filter Component" spec
 * are out of scope.
 *
 * Input: digits only, at most 8 of them, grouped with commas as the user types and shown with a
 * fixed "$" in front once there is a value. On focus the caret goes to the start of an empty field
 * and to the end of one that already has a value. A blank field means "no minimum" ($0) / "no
 * maximum".
 *
 * Validation: when the user leaves a field or presses Enter and the minimum ends up greater than the
 * maximum, the Maximum is cleared and goes back to its placeholder, regardless of which of the two
 * fields the user edited — the Minimum entered is always kept.
 *
 * Emits `{ min, max }` — numbers, or null for a blank field — only when a field settles (Enter or
 * blur, after validation) and only when the range actually changed.
 *
 * Only produces markup (`getHtml`) and binds behavior to the element the calling control created
 * from it (`new PriceFilter(element)`); it never creates DOM itself.
 */
export default class PriceFilter {
  static getSelector() {
    return '[data-price-filter]';
  }

  static getHtml() {
    return `
      <div class="nhs-c-input__group" data-price-filter data-qa="price_filter">
        <p class="nhs-c-input__label nhs-c-input__label--book">Price</p>
        <div class="nhs-c-input__range">
          ${createFieldHtml({ field: 'min', label: 'Minimum', placeholder: 'Min' })}
          <span class="nhs-c-input__range-separator" aria-hidden="true">–</span>
          ${createFieldHtml({ field: 'max', label: 'Maximum', placeholder: 'Max' })}
        </div>
      </div>
    `;
  }

  constructor(element) {
    this.element = element;
    this.value$ = new Subject();
    this.listeners = [];
    this.lastEmitted = { min: null, max: null };

    this.fields = Object.fromEntries(FIELDS.map((field) => {
      const container = element.querySelector(`[data-price-field="${field}"]`);
      return [field, {
        container,
        input: container.querySelector('[data-price-input]'),
        prefix: container.querySelector('[data-prefix]'),
      }];
    }));

    FIELDS.forEach((field) => this.bindField(field));
  }

  addListener(target, type, handler) {
    target.addEventListener(type, handler);
    this.listeners.push({ target, type, handler });
  }

  bindField(field) {
    const { container, input } = this.fields[field];

    this.addListener(input, 'input', () => {
      const digits = formatNumericInput(input);
      this.setPrefixVisible(field, digits.length > 0);
    });

    this.addListener(input, 'focus', () => {
      // After the browser has placed the caret for the click that focused the field.
      requestAnimationFrame(() => {
        const caret = input.value ? input.value.length : 0;
        input.setSelectionRange(caret, caret);
      });
    });

    this.addListener(input, 'blur', () => this.settle());
    this.addListener(input, 'keydown', (event) => {
      if (event.key === 'Enter') {
        this.settle();
      }
    });

    // The "$" and the padding are part of the field but not of the input.
    this.addListener(container, 'click', (event) => {
      if (event.target !== input) {
        input.focus();
      }
    });
  }

  setPrefixVisible(field, isVisible) {
    this.fields[field].prefix.toggleAttribute('hidden', !isVisible);
  }

  clearField(field) {
    this.fields[field].input.value = '';
    this.setPrefixVisible(field, false);
  }

  getRange() {
    return {
      min: parseDigits(this.fields.min.input.value),
      max: parseDigits(this.fields.max.input.value),
    };
  }

  settle() {
    if (hasMinMaxConflict(this.getRange())) {
      this.clearField('max');
    }

    const range = this.getRange();
    if (range.min === this.lastEmitted.min && range.max === this.lastEmitted.max) {
      return;
    }

    this.lastEmitted = range;
    this.value$.next(range);
  }

  getObservable() {
    return this.value$.asObservable();
  }

  // Syncs the fields to an externally-known value (e.g. the store's current filter, read back
  // when the modal reopens) without going through value$ — this reflects state that already
  // exists, it isn't a new user edit to emit.
  setValue({ min, max }) {
    [['min', min], ['max', max]].forEach(([field, value]) => {
      if (value == null) {
        this.clearField(field);
        return;
      }
      this.fields[field].input.value = formatWithCommas(String(value));
      this.setPrefixVisible(field, true);
    });
    this.lastEmitted = { min, max };
  }

  destroy() {
    this.listeners.forEach(({ target, type, handler }) => target.removeEventListener(type, handler));
  }
}
